import  { AllowedVPCStage, MixedcodesEnvironment, getMixCodeURI, mixedcodesSwitchAllowedStages, MixedcodesCodeType } from '../../../../types/componentTypes/mixcodes-types';
import { MixCodesParameters } from '../../../../types/configuration';

const productionMixcodesDomain = 'https://mixcodes-internal.coke.com/pcservices/rest/v7';

export const usingVPC = () => (process.env.REACT_APP_AWS_STAGE in AllowedVPCStage ? true : false);

/**
 * Determines if changing the mixcodes environment is allowed based on the current AWS stage.
 *
 * This function checks the value of the `REACT_APP_AWS_STAGE` environment variable
 * and returns `true` if the stage is set to `'gamma'`.
 * @returns {boolean} - Returns `true` if the current AWS stage is `'gamma'`, otherwise `false`.
 */
export const isSwitchEnvironmentAllowed = (): boolean => (mixedcodesSwitchAllowedStages.includes(process.env.REACT_APP_AWS_STAGE));

/**
 * Retrieves the appropriate MixCodes URL based on the environment and code type.
 *
 * This function determines the correct URL to use for MixCodes based on the provided environment
 * (`Test` or `Production`) and the code type. It also considers whether switching environments
 * is allowed (`isSwitchEnvironmentAllowed`) and whether the application is using a VPC (`usingVPC`).
 *
 * - If switching environments is allowed:
 *   - Returns the VPC URL (`uriVPCMap`) for `Production`.
 *   - Returns the non-VPC URL (`uriMap`) for `Test`.
 * - If switching environments is not allowed:
 *   - Returns the VPC URL (`uriVPCMap`) if the application is using a VPC.
 *   - Returns the non-VPC URL (`uriMap`) otherwise.
 *
 * @param {string} codeType - The type of code for which the URL is being retrieved(`Pincodes` or `Viral Codes`).
 * @param {MixedcodesEnvironment} environment - The current environment (`Test` or `Production`).
 * @returns {string} - The appropriate MixCodes URL based on the environment and code type.
 */
export const getMixedcodesUrl = (codeType: MixedcodesCodeType, environment?: MixedcodesEnvironment): string => {
    if (isSwitchEnvironmentAllowed()) {
        return environment === 'Production' ? getMixCodeURI.uriVPCMap[codeType] : getMixCodeURI.uriMap[codeType];
    } else {


        return usingVPC() ? getMixCodeURI.uriVPCMap[codeType] : getMixCodeURI.uriMap[codeType];
    }
};

/**
 * Determines the current MixCodes environment based on the URI in the provided parameters.
 *
 * This function checks if the `uri` property of `mixCodesParameters` starts with the first 40 characters
 * ()
 * of the VPC Pincodes URI. If it does, it returns `'Production'`; otherwise, it returns `'Test'`.
 *
 * @param {MixCodesParameters} mixCodesParameters - The parameters containing the MixCodes URI.
 * @returns {MixedcodesEnvironment} - The current environment (`'Production'` or `'Test'`).
 */
export const getCurrentMixedcodesEnvironment = (mixCodesParameters: MixCodesParameters): MixedcodesEnvironment => {
    return mixCodesParameters?.uri?.startsWith(productionMixcodesDomain) ? 'Production' : 'Test';
};
